import { useMemo } from "react";
import { useI18n, type Lang } from "../i18n.js";

/** Words for the Conversations and Statistics screens, kept apart from the shared dictionary (English and Spanish). */
const en = {
  "nav.statistics": "Statistics",
  "nav.conversations": "Conversations",
  "stats.title": "Statistics",
  "stats.lead": "What your agents are doing. Test chats from the preview are not counted.",
  "filters.agent": "Agent",
  "filters.allAgents": "All agents",
  "filters.from": "From",
  "filters.to": "To",
  "filters.last7": "Last 7 days",
  "filters.last30": "Last 30 days",
  "filters.last90": "Last 90 days",
  "filters.search": "Search conversations",
  "filters.searchPlaceholder": "Words in a conversation",
  "filters.showTests": "Show test chats",
  "filters.label": "Filters",
  "stats.conversations": "Conversations",
  "stats.messages": "Messages",
  "stats.avgMessages": "Messages per conversation",
  "stats.avgMinutes": "Average minutes per conversation",
  "stats.totalMinutes": "Total minutes",
  "stats.leads": "Leads captured",
  "stats.knowledge": "Answered from your knowledge",
  "stats.unanswered": "Could not answer",
  "stats.ofAnswers": "{n} of {total} answers",
  "stats.thumbs": "Visitor thumbs",
  "stats.thumbsValue": "{up} up, {down} down",
  "stats.noAnswers": "No answers yet",
  "stats.accuracyNote": "We do not claim one “accuracy” number. Look at how often answers come from your knowledge, how often the agent could not answer, and the visitors' thumbs.",
  "stats.perDay": "Conversations per day",
  "stats.day": "Day",
  "stats.minutes": "Minutes",
  "stats.empty": "No conversations in this period.",
  "stats.top": "Most frequent questions",
  "stats.topEmpty": "No questions yet.",
  "stats.unansweredList": "Questions the agent could not answer",
  "stats.times": "Times",
  "stats.question": "Question",
  "stats.numbersOnly": "Your role sees numbers only. Ask an account owner or manager to read the conversations.",
  "stats.loading": "Loading…",
  "stats.error": "Could not load the statistics.",
  "export.title": "Download",
  "export.stats": "Download statistics",
  "export.list": "Download these conversations",
  "export.one": "Download this conversation",
  "export.csv": "CSV (spreadsheet)",
  "export.pdf": "PDF",
  "export.md": "Markdown (.md)",
  "export.busy": "Preparing…",
  "export.done": "File downloaded.",
  "export.tooLarge": "That is too much for one file. Choose a shorter period or one agent.",
  "export.limited": "Too many downloads. Try again in a while.",
  "export.serverBusy": "Many downloads are being prepared. Try again in a few seconds.",
  "export.forbidden": "Your role cannot download conversations.",
  "export.failed": "Could not prepare the file. Try again in a moment.",
  "retention.title": "How long conversations are kept",
  "retention.help": "Older conversations are deleted. You can also delete one conversation at any time. Numbers per day stay.",
  "retention.label": "Keep conversations for",
  "retention.days": "{n} days",
  "retention.months": "{n} months",
  "retention.year": "1 year",
  "retention.years": "{n} years",
  "retention.saved": "Saved.",
  "retention.forbidden": "Only an account owner can change this.",
  "conv.title": "Conversations",
  "conv.lead": "What visitors asked and what your agents answered.",
  "conv.started": "Started",
  "conv.agent": "Agent",
  "conv.firstQuestion": "First question",
  "conv.messages": "Messages",
  "conv.minutes": "Minutes",
  "conv.lead.badge": "Lead",
  "conv.test": "Test chat",
  "conv.none": "No conversations yet.",
  "conv.noMatch": "No conversations match these filters.",
  "conv.showing": "Showing {n} of {total}",
  "conv.more": "Show more",
  "conv.deletedAgent": "Deleted agent",
  "conv.forbidden": "Your role sees numbers only, not conversations. Ask an account owner or manager.",
  "conv.error": "Could not load the conversations.",
  "conv.back": "All conversations",
  "conv.visitor": "Visitor",
  "conv.agentSays": "Agent",
  "conv.answerKnowledge": "From your knowledge",
  "conv.answerNone": "Could not answer",
  "conv.answerGeneral": "General answer",
  "conv.answerFailed": "Answer interrupted",
  "conv.thumbUp": "Visitor marked this answer as good",
  "conv.thumbDown": "Visitor marked this answer as bad",
  "conv.channel": "Channel",
  "conv.notFound": "This conversation does not exist or was deleted.",
  "conv.delete": "Delete this conversation",
  "conv.deleteConfirm": "Delete this conversation for good? The messages cannot be recovered.",
  "conv.deleteYes": "Yes, delete it",
  "conv.cancel": "Cancel",
  "conv.deleted": "Conversation deleted.",
  "conv.deleteFailed": "Could not delete it.",
  "conv.timeline": "Conversation",
  "src.title": "Where visitors came from",
  "src.lead": "The first thing that brought each visitor to your site during their visit (an ad, an e-mail, a search, another site, or a direct visit).",
  "src.channel": "Channel",
  "src.source": "Source",
  "src.medium": "Medium",
  "src.campaign": "Campaign",
  "src.conversations": "Conversations",
  "src.leads": "Leads",
  "src.leadRate": "Lead rate",
  "src.landing": "Top landing pages",
  "src.submission": "Top pages where people talk",
  "src.page": "Page",
  "src.campaigns": "Campaigns",
  "src.sources": "Sources",
  "src.empty": "No conversations in this period.",
  "src.note": "“Unknown (before tracking)” are conversations from before this was recorded. “Direct” can also be a visit whose source the browser did not share.",
  "src.column": "Source",
  "src.unknown": "Unknown",
  "src.how": "How the visitor arrived",
  "src.landingUrl": "Landing page",
  "src.conversationPage": "Page of the conversation",
  "src.cameFrom": "Came from",
  "src.clickIds": "Click ids",
  "src.sourceMedium": "Source / medium",
  "src.confidence.high": "Reliable: the address carried campaign tags or an ad click id.",
  "src.confidence.medium": "Probable: worked out from the site the visitor came from.",
  "src.confidence.low": "Uncertain: no tags and no clear referrer.",
  "src.firstSeenBy": "Recorded from the first page where the chat window ran, which may not be the very first page of the visit.",
  "src.derived": "Worked out afterwards from older data.",
  "ch.paid_search": "Paid search",
  "ch.paid_social": "Paid social",
  "ch.display": "Display ads",
  "ch.email": "E-mail",
  "ch.social": "Social",
  "ch.organic_search": "Organic search",
  "ch.referral": "Other websites",
  "ch.affiliate": "Affiliates",
  "ch.campaign": "Other campaigns",
  "ch.direct": "Direct",
  "ch.unknown": "Unknown",
  "ch.unknown_before": "Unknown (before tracking)",
} as const;

export type AKey = keyof typeof en;

const es: Partial<Record<AKey, string>> = {
  "nav.statistics": "Estadísticas",
  "nav.conversations": "Conversaciones",
  "stats.title": "Estadísticas",
  "stats.lead": "Lo que hacen tus agentes. Los chats de prueba de la vista previa no se cuentan.",
  "filters.agent": "Agente",
  "filters.allAgents": "Todos los agentes",
  "filters.from": "Desde",
  "filters.to": "Hasta",
  "filters.last7": "Últimos 7 días",
  "filters.last30": "Últimos 30 días",
  "filters.last90": "Últimos 90 días",
  "filters.search": "Buscar conversaciones",
  "filters.searchPlaceholder": "Palabras de una conversación",
  "filters.showTests": "Mostrar chats de prueba",
  "filters.label": "Filtros",
  "stats.conversations": "Conversaciones",
  "stats.messages": "Mensajes",
  "stats.avgMessages": "Mensajes por conversación",
  "stats.avgMinutes": "Minutos promedio por conversación",
  "stats.totalMinutes": "Minutos totales",
  "stats.leads": "Contactos captados",
  "stats.knowledge": "Respondidas desde tu conocimiento",
  "stats.unanswered": "No supo responder",
  "stats.ofAnswers": "{n} de {total} respuestas",
  "stats.thumbs": "Pulgares de los visitantes",
  "stats.thumbsValue": "{up} arriba, {down} abajo",
  "stats.noAnswers": "Aún no hay respuestas",
  "stats.accuracyNote": "No prometemos un único número de “precisión”. Mira con qué frecuencia las respuestas salen de tu conocimiento, cuántas veces el agente no supo responder y los pulgares de los visitantes.",
  "stats.perDay": "Conversaciones por día",
  "stats.day": "Día",
  "stats.minutes": "Minutos",
  "stats.empty": "No hay conversaciones en este período.",
  "stats.top": "Preguntas más frecuentes",
  "stats.topEmpty": "Aún no hay preguntas.",
  "stats.unansweredList": "Preguntas que el agente no supo responder",
  "stats.times": "Veces",
  "stats.question": "Pregunta",
  "stats.numbersOnly": "Tu rol ve solo números. Pide a un propietario o gestor de la cuenta que lea las conversaciones.",
  "stats.loading": "Cargando…",
  "stats.error": "No se pudieron cargar las estadísticas.",
  "export.title": "Descargar",
  "export.stats": "Descargar estadísticas",
  "export.list": "Descargar estas conversaciones",
  "export.one": "Descargar esta conversación",
  "export.csv": "CSV (hoja de cálculo)",
  "export.pdf": "PDF",
  "export.md": "Markdown (.md)",
  "export.busy": "Preparando…",
  "export.done": "Archivo descargado.",
  "export.tooLarge": "Es demasiado para un solo archivo. Elige un período más corto o un solo agente.",
  "export.limited": "Demasiadas descargas. Inténtalo de nuevo en un rato.",
  "export.serverBusy": "Se están preparando muchas descargas. Inténtalo de nuevo en unos segundos.",
  "export.forbidden": "Tu rol no puede descargar conversaciones.",
  "export.failed": "No se pudo preparar el archivo. Inténtalo de nuevo en un momento.",
  "retention.title": "Cuánto tiempo se guardan las conversaciones",
  "retention.help": "Las conversaciones más antiguas se borran. También puedes borrar una conversación en cualquier momento. Los números por día se conservan.",
  "retention.label": "Guardar las conversaciones durante",
  "retention.days": "{n} días",
  "retention.months": "{n} meses",
  "retention.year": "1 año",
  "retention.years": "{n} años",
  "retention.saved": "Guardado.",
  "retention.forbidden": "Solo un propietario de la cuenta puede cambiar esto.",
  "conv.title": "Conversaciones",
  "conv.lead": "Lo que preguntaron los visitantes y lo que respondieron tus agentes.",
  "conv.started": "Inicio",
  "conv.agent": "Agente",
  "conv.firstQuestion": "Primera pregunta",
  "conv.messages": "Mensajes",
  "conv.minutes": "Minutos",
  "conv.lead.badge": "Contacto",
  "conv.test": "Chat de prueba",
  "conv.none": "Aún no hay conversaciones.",
  "conv.noMatch": "Ninguna conversación coincide con estos filtros.",
  "conv.showing": "Mostrando {n} de {total}",
  "conv.more": "Mostrar más",
  "conv.deletedAgent": "Agente eliminado",
  "conv.forbidden": "Tu rol ve solo números, no conversaciones. Pide a un propietario o gestor de la cuenta.",
  "conv.error": "No se pudieron cargar las conversaciones.",
  "conv.back": "Todas las conversaciones",
  "conv.visitor": "Visitante",
  "conv.agentSays": "Agente",
  "conv.answerKnowledge": "Desde tu conocimiento",
  "conv.answerNone": "No supo responder",
  "conv.answerGeneral": "Respuesta general",
  "conv.answerFailed": "Respuesta interrumpida",
  "conv.thumbUp": "El visitante marcó esta respuesta como buena",
  "conv.thumbDown": "El visitante marcó esta respuesta como mala",
  "conv.channel": "Canal",
  "conv.notFound": "Esta conversación no existe o se eliminó.",
  "conv.delete": "Eliminar esta conversación",
  "conv.deleteConfirm": "¿Eliminar esta conversación para siempre? Los mensajes no se pueden recuperar.",
  "conv.deleteYes": "Sí, eliminarla",
  "conv.cancel": "Cancelar",
  "conv.deleted": "Conversación eliminada.",
  "conv.deleteFailed": "No se pudo eliminar.",
  "conv.timeline": "Conversación",
  "src.title": "De dónde llegaron los visitantes",
  "src.lead": "Lo primero que llevó a cada visitante a tu sitio durante su visita (un anuncio, un correo, una búsqueda, otro sitio o una visita directa).",
  "src.channel": "Canal",
  "src.source": "Fuente",
  "src.medium": "Medio",
  "src.campaign": "Campaña",
  "src.conversations": "Conversaciones",
  "src.leads": "Contactos",
  "src.leadRate": "Tasa de contactos",
  "src.landing": "Páginas de llegada más frecuentes",
  "src.submission": "Páginas donde más se conversa",
  "src.page": "Página",
  "src.campaigns": "Campañas",
  "src.sources": "Fuentes",
  "src.empty": "No hay conversaciones en este período.",
  "src.note": "“Desconocido (antes del seguimiento)” son conversaciones de antes de que esto se registrara. “Directo” también puede ser una visita cuyo origen el navegador no compartió.",
  "src.column": "Fuente",
  "src.unknown": "Desconocido",
  "src.how": "Cómo llegó el visitante",
  "src.landingUrl": "Página de llegada",
  "src.conversationPage": "Página de la conversación",
  "src.cameFrom": "Venía de",
  "src.clickIds": "Identificadores de clic",
  "src.sourceMedium": "Fuente / medio",
  "src.confidence.high": "Fiable: la dirección traía etiquetas de campaña o un identificador de clic de anuncio.",
  "src.confidence.medium": "Probable: deducido del sitio de donde venía el visitante.",
  "src.confidence.low": "Incierto: sin etiquetas y sin una referencia clara.",
  "src.firstSeenBy": "Registrado desde la primera página donde se ejecutó el chat, que puede no ser la primera página de la visita.",
  "src.derived": "Deducido después a partir de datos antiguos.",
  "ch.paid_search": "Búsqueda de pago",
  "ch.paid_social": "Redes sociales de pago",
  "ch.display": "Anuncios gráficos",
  "ch.email": "Correo",
  "ch.social": "Redes sociales",
  "ch.organic_search": "Búsqueda orgánica",
  "ch.referral": "Otros sitios web",
  "ch.affiliate": "Afiliados",
  "ch.campaign": "Otras campañas",
  "ch.direct": "Directo",
  "ch.unknown": "Desconocido",
  "ch.unknown_before": "Desconocido (antes del seguimiento)",
};

export type AText = (key: AKey, vars?: Record<string, string | number>) => string;

export const analyticsDictionaries = { en, es } as const;

export function analyticsText(lang: Lang): AText {
  return (key, vars) => {
    const raw = (lang === "es" ? es[key] : undefined) ?? en[key];
    return vars ? raw.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? "")) : raw;
  };
}

export function useAnalyticsText(): { t: AText; lang: Lang } {
  const { lang } = useI18n();
  return useMemo(() => ({ t: analyticsText(lang), lang }), [lang]);
}

